// Your Personal Clone's form (live's `your-personal-clone-form` library view, spec G14-templates
// PR 13): a photo of yourself, your voice (your own voices or a new one, recorded or uploaded),
// the script (Generate Script with its product, Enhance Script), and the voiceover Generate
// Audio makes, which the run then animates. One tile.
import { Group, Stack, Text, Textarea } from "@mantine/core";
import { useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { Voice } from "../../../api/generated/model";
import { VideoTemplateQuote, VideoTemplateRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import type { PickedImage } from "../../../platform/media/ImagePicker";
import { ScriptActions, ScriptBusy } from "../../../platform/scripts/ScriptActions";
import { useScriptJobs } from "../../../platform/scripts/use-script-jobs";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { GlassPanel } from "../../../ui/GlassPanel";
import { AUDIO_NOTE, CostChip } from "../TemplateCost";
import { PortraitTile } from "./ActorTile";
import { ReuseSwitch, StartArrow, type TemplateFormProps } from "./kit";
import { ScriptOverlayWithProduct } from "./ScriptOverlayWithProduct";
import { useVoiceover, VoiceAudioTile, VoiceoverButton, VoiceWaits } from "./voiceover";

/** Live's words (`library-view/constants.ts`, `schema.ts`, `usePersonalCloneFormValue`). */
const ACTOR_TIP =
  "Your personal AI double. Simply upload a photo of yourself — no full actor creation flow needed.";
const VOICE_TIP =
  "Your cloned voice. Only recording is available here, since this is meant to capture your real voice.";
const SCRIPT_PLACEHOLDER =
  "Write your script... Use [emotion] to add emotions like [laughs], [sights], [excited]";
const BRIEF_PLACEHOLDER = "Describe what your clone should talk about — the product, the audience, the tone…";
const NO_MODEL = "No talking-head model is available for this team";
/** Live's talking-head model (`DEFAULT_TALKING_HEAD_MODEL_ID`), the run's head. */
const HEAD_MODEL = "veed-fabric-1-0";
const SCRIPT_MAX = 10000;
/** Live's Create Voice in this form: two tabs, its own words, no Design. */
const CREATE_TABS = [
  { value: "record", label: "Record Audio" },
  { value: "upload", label: "Upload Audio" },
] as const;

/**
 * Live's form: "Reuse example" (off and disabled: the example follows), the AI Actor tile (an
 * upload) and the voice tile (Custom Voices, Create Voice; once made, the voiceover's player),
 * the script with its two tools, Generate Audio (the voiceover, made before the run: the start
 * waits for it, live's "Generate the audio first"), and the round start, with no tooltip, as
 * live's. While the voiceover is made or the run starts, everything but the voice tile's loader
 * is greyed, as live's (`isBusy`).
 */
export function PersonalCloneForm(props: TemplateFormProps) {
  const { teamId, brandId, above, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const [portrait, setPortrait] = useState<PickedImage | null>(null);
  const [voice, setVoice] = useState<Voice | null>(null);
  const [script, setScript] = useState("");
  const [voicePanel, setVoicePanel] = useState(false);
  const [scriptOpen, setScriptOpen] = useState(false);
  const catalog = useGetCatalog().data;
  const voiceover = useVoiceover(teamId, brandId);
  // A new script or another voice is not what the voiceover says: it goes (live's
  // `useInvalidateAudio`); the same voice picked again keeps it, as live's.
  const write = (text: string) => {
    setScript(text);
    voiceover.clear();
  };
  const chooseVoice = (next: Voice | null) => {
    if (next?.id !== voice?.id) voiceover.clear();
    setVoice(next);
  };
  const scripts = useScriptJobs({ teamId, brandId, tool: "templates", onScript: write });
  const busyScript = scripts.processing || scripts.enhancing;
  const missing =
    catalog !== undefined && !catalog.video_models.some((each) => each.id === HEAD_MODEL) ? NO_MODEL : null;
  const made = voiceover.made;
  const inputs =
    portrait === null || made === null || missing !== null
      ? null
      : {
          template: "your-personal-clone" as const,
          actor: { kind: "asset" as const, asset_id: portrait.assetId },
          audio: made.ref,
        };
  const body = inputs === null ? null : { type: "video.template" as const, inputs };
  const request = VideoTemplateRequest.safeParse(body);
  const quote = VideoTemplateQuote.safeParse(body);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const busy = voiceover.running || job.start.isPending;
  return (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      // No Reuse example yet: live's carries a template voice (G14's gap), so the switch stays off.
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Your Personal Clone</Text>}
      overlay={
        scriptOpen ? (
          <ScriptOverlayWithProduct
            teamId={teamId}
            brandId={brandId}
            placeholder={BRIEF_PLACEHOLDER}
            pending={scripts.generating}
            error={scripts.generateError}
            onGenerate={(brief, productId) => scripts.generate(brief, productId, () => setScriptOpen(false))}
            onCancel={() => setScriptOpen(false)}
          />
        ) : undefined
      }
      aside={
        voicePanel ? (
          <VoicePanel
            teamId={teamId}
            brandId={brandId}
            chosen={voice}
            // Locked while a voiceover is made: another voice would forget a speech being paid for.
            onChoose={(next) => (voiceover.running ? undefined : chooseVoice(next))}
            head={<VoiceWaits running={voiceover.running} />}
            onClose={() => setVoicePanel(false)}
            designModel={catalog?.audio_models.find((each) => each.job_types.includes("voice.design"))}
            only={["team", "create"]}
            createTabs={CREATE_TABS}
          />
        ) : undefined
      }
      footer={
        <Group justify="flex-end" gap={8} wrap="nowrap">
          {/* Live's: one output, its voiceover billed by Generate Audio on its own. */}
          <CostChip estimate={props.template.estimate} outputs={1} note={AUDIO_NOTE} quote={job.lastPriced} />
          <StartArrow canStart={job.canStart && !busy} starting={job.start.isPending} onStart={job.startJob} />
        </Group>
      }
    >
      <Stack gap={12}>
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <PortraitTile
            teamId={teamId}
            brandId={brandId}
            value={portrait}
            tip={ACTOR_TIP}
            disabled={busy}
            onChange={setPortrait}
          />
          <VoiceAudioTile
            voiceName={voice?.name ?? null}
            voiceover={voiceover}
            tip={VOICE_TIP}
            disabled={job.start.isPending}
            onOpen={() => setVoicePanel(true)}
          />
        </Group>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={500}>
            Script
          </Text>
          <ScriptActions
            canGenerate={!busyScript && !busy}
            canEnhance={!busyScript && !busy && script.trim() !== ""}
            enhancing={scripts.enhancing}
            failed={scripts.scriptFailed}
            onGenerate={() => setScriptOpen(true)}
            onEnhance={() => scripts.enhance(script)}
          />
        </Group>
        <ScriptBusy busy={scripts.processing}>
          <Textarea
            variant="unstyled"
            aria-label="Script"
            placeholder={SCRIPT_PLACEHOLDER}
            autosize
            minRows={2}
            maxRows={8}
            maxLength={SCRIPT_MAX}
            disabled={scripts.processing || busy}
            value={script}
            onChange={(event) => write(event.currentTarget.value)}
          />
        </ScriptBusy>
        <VoiceoverButton
          voiceover={voiceover}
          script={script}
          voiceId={voice?.id ?? null}
          disabled={busyScript || job.start.isPending}
        />
        {missing === null ? null : (
          <Text size="sm" c="var(--cai-color-text-danger)" role="alert">
            {missing}
          </Text>
        )}
        {voiceover.error === null ? null : <ErrorAlert error={voiceover.error} />}
        {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
        {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
        {/* Live's toast, said in the panel (web/AGENTS.md: no toasts). */}
        {job.start.isSuccess ? (
          <Text component="output" size="sm">
            Generation started
          </Text>
        ) : null}
      </Stack>
    </GlassPanel>
  );
}
